<template>
  <div>
    <div class="page-head">
      <div>
        <h1 class="page-head__title">{{ t("changelog.title") }}</h1>
        <p class="page-head__lede">{{ t("changelog.lede") }}</p>
      </div>
    </div>
    <p class="muted" style="display:none">
      What changed in <code>v1</code>, newest first. Within v1 only additive
      change is allowed — new endpoints, new optional parameters, new response
      fields. Anything that removes a field, changes a type, tightens validation
      or changes a default is a <code>v2</code>.
    </p>

    <div class="card">
      <h2 class="card__title">{{ t("changelog.promise") }}</h2>
      <ul class="changelog__promise">
        <li>New fields can appear in any response. Ignore what you do not recognise.</li>
        <li>New optional query parameters can appear. Existing defaults do not change.</li>
        <li>New endpoints and new webhook topics can appear.</li>
        <li>New error <code>code</code> values can appear within an existing HTTP status.</li>
        <li>
          Nothing is removed, retyped or narrowed. When v2 ships, v1 is supported for
          twelve months, with the date advertised in <code>Deprecation</code> and
          <code>Sunset</code> headers on every affected response.
        </li>
      </ul>
    </div>

    <div v-for="entry in entries" :key="entry.date" class="card">
      <h3>
        {{ entry.date }}
        <span v-if="entry.tag" class="changelog__tag">{{ entry.tag }}</span>
      </h3>
      <ul>
        <li v-for="(change, i) in entry.changes" :key="i">
          <strong class="changelog__kind" :class="`changelog__kind--${change.kind}`">
            {{ change.kind }}
          </strong>
          {{ change.text }}
        </li>
      </ul>
    </div>
  </div>
</template>

<script setup>
import { useI18n } from "vue-i18n";
const { t } = useI18n();

/**
 * The changelog, written by hand.
 *
 * DELIBERATELY NOT GENERATED. A diff of two OpenAPI documents can say that a
 * field appeared; it cannot say whether that matters to you, or what to do about
 * it. The entries below are the sentences a developer needs, and the
 * compatibility promise above them is the contract those sentences are written
 * against — it is at the top because it is the part that is true regardless of
 * how far anyone scrolls.
 *
 * `kind` is one of: added, changed, fixed, deprecated, security.
 */
const entries = [
  {
    date: "2026-10-26",
    changes: [
      { kind: "added", text: "Unit events: declare events in the version manifest (events, with the units.events:write scope) and publish them with POST /units/{unit_id}/events. Customers write alert rules on them with the App event template; a matching rule's event.created carries data.app_event. Uninstalling switches those rules off. See the guide \"Publish events that raise alerts\"." }
    ]
  },
  {
    date: "2026-10-25",
    changes: [
      { kind: "added", text: "RAW_BINARY device command for binary protocols: params { message_id, body_hex }; the platform adds the header, serial number, terminal id, checksum and escaping. JT/T 808 is the first protocol that takes it; any other is refused with EXT_COMMAND_UNSUPPORTED_BY_PROTOCOL. See \"Binary protocols: RAW_BINARY\" in the guide \"Send a command and track its result\"." },
      { kind: "fixed", text: "POST /devices/{device_id}/commands now reads params given as plain values, as documented; a CUSTOM command's message was dropped and refused as missing." }
    ]
  },
  {
    date: "2026-10-24",
    changes: [
      { kind: "added", text: "Webhook topic device.report, with the new scope devices.reports:read: a report or response from a device that is not a position, with the device's report code and result code verbatim and the rest as fields. Report codes depend on the device protocol; Queclink (GTSOD, GTVDO, GTPIC, GTRFQ, GTFTP, GTAWS) is the first. See \"device.report\" in the Webhooks guide." },
      { kind: "added", text: "Manifest capabilities. A version may declare capabilities: [\"microphone\"]; the host then frames that version with allow=\"fullscreen; clipboard-write; microphone\" so the app can capture audio (push-to-talk, voice input). The manifest check accepts microphone only and notes the request in its message. Every other app keeps the same allow list and sandbox." }
    ]
  },
  {
    date: "2026-10-21",
    changes: [
      { kind: "added", text: "Device command results. POST /devices/{device_id}/commands answers { command_id, status: \"queued\", created_at }; GET /devices/{device_id}/commands/{command_id} and GET /devices/{device_id}/commands?status=… return the status (queued, sent, then accepted, rejected, unconfirmed, timeout, expired or failed), terminal, status_reason and timestamps. See the guide \"Send a command and track its result\"." },
      { kind: "added", text: "The device's reply, when it sends one, as response on the command: raw is always kept, and data is the parsed result where the platform knows the format. The first read command is Galileosky \"Read registered cards\" (READ_CARDS), which returns { cards: [{ index, id }], slots }." },
      { kind: "added", text: "devices.commands:read for reading command history and results; devices:read and devices.commands:execute are accepted on the same reads. Webhook topic command.status_changed fires on every status change; command.completed and command.failed now carry the full command record, and exactly one of them fires per command." }
    ]
  },
  {
    date: "2026-10-10",
    tag: "v2 platform",
    changes: [
      { kind: "added", text: "The Zarokaa hub: register an app once and it reaches every installation you distribute to, ours and white-label partners', within about a minute. GET /api/registry/v1/directory on the hub lists every listed installation with its api_base, api_bases and jwks_url." },
      { kind: "added", text: "This portal now runs on the hub. Sign-in has TOTP two-factor authentication with backup codes, and sign-up is by invitation by default (an operator can switch to open sign-up with e-mail verification). A store listing editor per locale, team members, invitations and Notices are new." },
      { kind: "added", text: "Distribution: all installations or an allow-list, listed publicly or by link only. Testers are invited per installation and account; an unpublished app runs for its testers only." },
      { kind: "added", text: "Automatic review replaces manual approval: seven checks with evidence, publish on pass, nightly re-checks of live versions, and automatic suspensions or admin blocks that reach every installation within a minute. See the guide \"Review checks and publishing\"." },
      { kind: "added", text: "Alerts on Alert Engine V2: alerts:read (rules, incidents, incident history) and alerts:write (acknowledge, resolve). event.created carries the incident id; geofence.entered and geofence.exited come from the customer's geofence alert rules." },
      { kind: "changed", text: "notifications:read and notifications:write are renamed alerts:read and alerts:write, with no alias: a manifest that names notifications:* fails the manifest check. event.created now needs alerts:read instead of units.events:read. Both breaks land while v1 is still a preview." },
      { kind: "added", text: "Install token: a five-minute, single-use RS256 JWT verified against the issuing installation's JWKS and the hub directory replaces the HMAC-signed install handoff. See the guide \"One app, many Zarokaa installations\"." },
      { kind: "added", text: "OAuth authorize is now a page in the Zarokaa web app (${web}/app/oauth/authorize, advertised as authorization_endpoint in each installation's discovery document). install_id is required on it, and the gate's GET /oauth/authorize is gone." },
      { kind: "added", text: "Bridge context 1.1: theme_mode (light, dark, auto), brand, installation and tokens (--polygon-* CSS values). theme events follow OS flips in auto mode, locale events fire on a direction-only change, and host is the web origin that frames you. See the guide \"Embedded apps: theme, language and brand\"." },
      { kind: "deprecated", text: "The portal no longer shows or replays webhook deliveries. Use each installation's API with webhooks:manage: GET /webhooks/:id/deliveries and POST /webhooks/deliveries/:delivery_id/replay." },
      { kind: "security", text: "Two-factor authentication is required to submit a version; credentials can now be revoked outright as well as rotated. The hub stores and replicates only scrypt hashes of client secrets." }
    ]
  },
  {
    date: "2026-08-30",
    tag: "v1 preview",
    changes: [
      { kind: "added", text: "Webhooks: 17 topics, per-installation subscriptions, HMAC signing with rotation overlap, eight-attempt retry over ~24 hours, and replay from this portal." },
      { kind: "added", text: "Entitlement API at /me/entitlement, so your billing state shows in the customer's App Store without money passing through Zarokaa." },
      { kind: "added", text: "This reference, generated from the routing catalog, plus an API console and a Postman collection export." },
      { kind: "security", text: "Webhook endpoints are re-resolved at delivery time and refused if the hostname now points at a private address — a check-then-connect gap closed against DNS rebinding." }
    ]
  }
];
</script>

<style scoped>
.changelog__promise li { margin-bottom: 0.3rem; }
.changelog__tag { font-size: 0.72rem; background: var(--accent); color: var(--ink-invert); border-radius: 8px; padding: 0.05rem 0.45rem; margin-left: 0.5rem; vertical-align: middle; }
.changelog__kind { font-size: 0.72rem; border-radius: 3px; padding: 0 0.35rem; margin-right: 0.4rem; color: var(--ink-invert); text-transform: uppercase; }
.changelog__kind--added { background: var(--green-500); }
.changelog__kind--changed { background: var(--brand-500); }
.changelog__kind--fixed { background: var(--blue-500); }
.changelog__kind--deprecated { background: var(--amber-500); }
.changelog__kind--security { background: var(--red-500); }
</style>
